/**
 * Where the PHONE APP opens (the user, 2026-10-26: "signed out user is the new home page; signed
 * in user is to the last rave"). The shell decides at launch — the last rave page it remembers,
 * for somebody signed in; else `app.…/`, which in the app is Find a rave for everybody. The
 * page's part is to tell the shell who is signed in (`NativeSession`, the `session` message),
 * on every change — offline the remembered account counts as signed in. My raves is
 * `/my-raves` in the app (`myRavesPath`).
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { inNativeApp, keepWidgets, postWidgetWords, reportSession } from '@/lib/nativeBridge';
import { widgetWords } from '@/visitor/widgets';
import { useLocale } from './LocaleProvider';
import { useAuth } from './AuthProvider';

/** My raves' address: `/my-raves`, on the phone and the website — `/` is Upcoming raves (2026-10-27). */
export function myRavesPath(): string {
  return '/my-raves';
}

/** Mounted once, inside `AuthProvider`: the phone app's shell hears who is signed in. */
export function NativeSession() {
  const { user, loading } = useAuth();
  const signedIn = Boolean(user);
  const accountId = user?.id ?? null;
  const { locale } = useLocale();
  useEffect(() => {
    if (loading || !inNativeApp()) return;
    if (signedIn && accountId) reportSession(true, accountId);
    else reportSession(signedIn);
  }, [loading, signedIn, accountId]);
  // The widgets' words, in the reader's language — signed out too (step 79).
  useEffect(() => {
    if (inNativeApp()) postWidgetWords(widgetWords(locale));
  }, [locale]);
  // The raves this account attends, from the SERVER's answer (never the phone's copy): every
  // other rave leaves the widgets (2026-10-30 — a rave left elsewhere stayed in them for good).
  const native = inNativeApp();
  const mine = useQuery({ queryKey: ['me', 'festivals'], queryFn: endpoints.myFestivals, enabled: native && signedIn, staleTime: 5 * 60_000 });
  const attended = mine.data && !mine.data.fromDevice
    ? mine.data.festivals.filter((f) => f.attending !== false).map((f) => f.editionId).sort().join(',')
    : null;
  useEffect(() => {
    if (native && signedIn && attended !== null) keepWidgets(attended ? attended.split(',') : []);
  }, [native, signedIn, attended]);
  return null;
}
